
import { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import CodeBlock from '@/components/ui/code-block';
import { Info, FileText, ArrowRight, Zap, Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatLocalDateTime } from '@/lib/date';

// Mock memory data
const mockMemories = [
  {
    id: 'mem_1a2b3c',
    content: "User created task: Finish onboarding flow by April 30th",
    timestamp: "2025-04-20T09:15:30Z",
    project: "Project X",
    user_id: "user123",
    semantic_score: 0.89,
    freshness_score: 0.95
  },
  {
    id: 'mem_4d5e6f',
    content: "User scheduled meeting with Jane on May 2nd at 2pm to review UX design drafts",
    timestamp: "2025-04-21T14:22:10Z",
    project: "Project X",
    user_id: "user123",
    semantic_score: 0.76,
    freshness_score: 0.92
  },
  {
    id: 'mem_7g8h9i',
    content: "User asked: What's my progress on Project Z? Team replied: 70% complete, awaiting final client approval",
    timestamp: "2025-04-22T11:05:45Z",
    project: "Project Z",
    user_id: "user123",
    semantic_score: 0.65,
    freshness_score: 0.87
  },
  {
    id: 'mem_10j11k',
    content: "Client feedback received: 'The onboarding process needs clearer instructions at step 3'",
    timestamp: "2025-04-23T15:30:22Z",
    project: "Project X",
    user_id: "user456",
    semantic_score: 0.91,
    freshness_score: 0.97
  },
  {
    id: 'mem_12l13m',
    content: "Team discussion: Need to improve API documentation for the authentication endpoints",
    timestamp: "2025-04-24T10:45:12Z",
    project: "Project Y",
    user_id: "user123",
    semantic_score: 0.82,
    freshness_score: 0.90
  }
];

// Mock example queries
const examplePrompts = [
  "What are my pending tasks for Project X?",
  "Summarize team discussions about API documentation",
  "What feedback did we receive about onboarding?",
  "Show me all scheduling information from the last week",
  "What's the progress on Project Z?"
];

// Code examples
const codeExamples = {
  javascript: `// Using Recallio's Context Orchestration API
import { RecallioClient } from 'recallio';

const recallio = new RecallioClient({ 
  apiKey: process.env.RECALLIO_API_KEY 
});

async function getOptimizedContext(query, maxTokens = 1000) {
  // Get optimized context block based on the query
  const contextResponse = await recallio.context.optimize({
    query: query,
    scope: {
      project_id: "proj_123",
      user_id: "user_456"
    },
    options: {
      max_tokens: maxTokens,
      distillation: true,  // Enable automatic summarization
      scoring: {
        semantic_weight: 0.7,  // Prioritize semantic relevance
        freshness_weight: 0.3  // With some weight on recency
      }
    }
  });
  
  return contextResponse;
}

// Usage with any LLM API
const optimizedContext = await getOptimizedContext(
  "What tasks are pending for Project X?"
);

console.log(\`Context size: \${optimizedContext.token_count} tokens\`);
console.log(\`Found \${optimizedContext.memories.length} relevant memories\`);
console.log(\`Distilled summary: \${optimizedContext.summary}\`);

// The optimizedContext.context field is ready to inject into your LLM prompt
`,

  python: `# Using Recallio's Context Engine with LangChain
from recallio import RecallioClient
from langchain.memory import RecallioContextMemory
from langchain_openai import ChatOpenAI

# Initialize Recallio client
recallio = RecallioClient(api_key="YOUR_RECALLIO_API_KEY")

# Set up context memory with optimization settings
context_memory = RecallioContextMemory(
    client=recallio,
    scope={"project_id": "proj_123", "user_id": "user_456"},
    token_limit=1000,
    enable_distillation=True,
    semantic_weight=0.8,
    freshness_weight=0.2
)

# Use with your favorite LLM
llm = ChatOpenAI(model="gpt-4o")

# The context engine will handle token optimization and context injection
response = llm.invoke(
    "What are the pending tasks for Project X?",
    context=context_memory
)

print(response)

# Access memory and scoring details for trust verification
print(f"Context used {context_memory.last_token_count} tokens")
print(f"Memory trace: {context_memory.last_trace}")
`
};

const ContextPlayground = () => {
  const [query, setQuery] = useState('');
  const [projectId, setProjectId] = useState('Project X');
  const [userId, setUserId] = useState('user123');
  const [maxTokens, setMaxTokens] = useState(1000);
  const [semanticWeight, setSemanticWeight] = useState(70);
  const [enableDistillation, setEnableDistillation] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState<'javascript' | 'python'>('javascript');
  const [openCodeExample, setOpenCodeExample] = useState(false);
  const [currentTab, setCurrentTab] = useState('test');
  const [optimizedContext, setOptimizedContext] = useState<{ 
    memories: Array<{ 
      id: string; 
      timestamp: string; 
      project: string; 
      content: string; 
      semantic_score?: number; 
      freshness_score?: number; 
    }>; 
    token_count?: number; 
    context_optimized?: string; 
    summary?: string; 
  } | null>(null);

  // Function to generate optimized context
  const generateOptimizedContext = () => {
    if (!query.trim()) {
      toast.error("Please enter a query");
      return;
    }

    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      // Find relevant memories based on query and scope
      const relevantMemories = findRelevantMemories();
      
      // Calculate token usage (mock)
      const tokensPerMemory = 65; // Approximate tokens per memory
      const totalTokens = relevantMemories.reduce((total, memory) => {
        return total + Math.ceil(memory.content.length / 4); // Rough estimate
      }, 0);
      
      // Generate distilled summary if enabled
      const distilledSummary = enableDistillation 
        ? generateDistilledSummary(relevantMemories)
        : null;
      
      // Build optimized context object
      const context = {
        memories: relevantMemories,
        token_count: totalTokens,
        max_tokens: maxTokens,
        summary: distilledSummary,
        context_raw: relevantMemories.map(m => m.content).join('\n\n'),
        context_optimized: distilledSummary || relevantMemories.map(m => m.content).join('\n\n'),
        scoring: {
          semantic_weight: semanticWeight / 100,
          freshness_weight: (100 - semanticWeight) / 100
        }
      };
      
      setOptimizedContext(context);
      setIsLoading(false);
      
      toast.success("Context optimized", {
        description: `Processed ${relevantMemories.length} memories into ${Math.min(totalTokens, maxTokens)} tokens`
      });
    }, 1500);
  };

  // Simple function to find mock relevant memories
  const findRelevantMemories = () => {
    // Filter by project and user if provided
    let filtered = mockMemories.filter(memory => {
      const projectMatch = projectId ? memory.project === projectId : true;
      const userMatch = userId ? memory.user_id === userId : true;
      return projectMatch && userMatch;
    });
    
    // Sort by combined score with provided weights
    const semanticWeightNorm = semanticWeight / 100;
    const freshnessWeightNorm = (100 - semanticWeight) / 100;
    
    filtered = filtered.sort((a, b) => {
      const scoreA = (a.semantic_score * semanticWeightNorm) + (a.freshness_score * freshnessWeightNorm);
      const scoreB = (b.semantic_score * semanticWeightNorm) + (b.freshness_score * freshnessWeightNorm);
      return scoreB - scoreA;
    });
    
    // Apply token budget constraints (simulate)
    let tokenCount = 0;
    const tokensPerMemory = 65; // Approximate tokens per memory
    
    return filtered.filter(memory => {
      const memoryTokens = Math.ceil(memory.content.length / 4); // Rough estimate
      if (tokenCount + memoryTokens <= maxTokens) {
        tokenCount += memoryTokens;
        return true;
      }
      return false;
    });
  };

  // Generate a distilled summary from memories
  const generateDistilledSummary = (memories: typeof mockMemories) => {
    if (memories.length === 0) return null;
    
    // This would be done by an LLM in production
    return `Summary of ${memories.length} memories:
- Tasks: ${memories.some(m => m.content.includes('task')) ? 'Finish onboarding flow (due Apr 30)' : 'None found'}
- Meetings: ${memories.some(m => m.content.includes('meeting')) ? 'Review with Jane on May 2' : 'None found'}
- Feedback: ${memories.some(m => m.content.includes('feedback')) ? 'Onboarding needs clearer step 3 instructions' : 'None found'}
- Projects: ${Array.from(new Set(memories.map(m => m.project))).join(', ')}`;
  };

  // Function to use an example prompt
  const useExamplePrompt = (prompt: string) => {
    setQuery(prompt);
  };

  return (
    <PageLayout
      title="Context Playground"
      description="Build and test optimized context blocks for your LLM applications. See how our context orchestration improves your AI's memory recall and reasoning."
      showBackToDashboard={true}
    >
      <div className="space-y-6">
        <Tabs value={currentTab} onValueChange={setCurrentTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="test">Test Context Engine</TabsTrigger>
            <TabsTrigger value="docs">Documentation</TabsTrigger>
          </TabsList>
          
          <TabsContent value="test" className="space-y-6">
            {/* Query and options panel */}
            <Card className="border border-recallio-light-gray/50">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="query">Query</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 px-2">
                              <Info size={14} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>The query used to find and optimize relevant context from your memory store.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Textarea
                      id="query"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Enter a query to optimize context for (e.g., 'What tasks are pending for Project X?')"
                      className="min-h-[80px] mt-2 font-geist-mono"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="projectId">Project ID</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 px-2">
                                <Info size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Scope memories to a specific project.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select
                        value={projectId}
                        onValueChange={setProjectId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a project" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Project X">Project X</SelectItem>
                          <SelectItem value="Project Y">Project Y</SelectItem>
                          <SelectItem value="Project Z">Project Z</SelectItem>
                          <SelectItem value="all">All Projects</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="userId">User ID</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 px-2">
                                <Info size={14} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Scope memories to a specific user.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Input
                        id="userId"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        placeholder="e.g., user_123"
                        className="font-geist-mono"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="maxTokens">Maximum Token Budget</Label>
                        <span className="text-xs font-geist-mono text-muted-foreground">{maxTokens} tokens</span>
                      </div>
                      <Slider 
                        value={[maxTokens]} 
                        onValueChange={(value) => setMaxTokens(value[0])}
                        max={4000}
                        min={100}
                        step={100}
                        className="py-4"
                      />
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="semanticWeight">Semantic vs. Freshness</Label>
                        <span className="text-xs font-geist-mono text-muted-foreground">{semanticWeight}% / {100-semanticWeight}%</span>
                      </div>
                      <Slider 
                        value={[semanticWeight]} 
                        onValueChange={(value) => setSemanticWeight(value[0])}
                        max={100}
                        step={5}
                        className="py-4"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center space-x-3">
                      <Switch
                        id="enableDistillation"
                        checked={enableDistillation}
                        onCheckedChange={setEnableDistillation}
                      />
                      <div>
                        <Label htmlFor="enableDistillation" className="cursor-pointer">Enable Context Distillation</Label>
                        <p className="text-xs text-muted-foreground mt-1">Automatically summarize memories to optimize token usage</p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={generateOptimizedContext}
                      disabled={isLoading || !query.trim()}
                      className="gap-2"
                    >
                      {isLoading ? (
                        <>
                          <Search className="h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4" />
                          Optimize Context
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Example prompts */}
            <div className="bg-recallio-dark-gray rounded-lg p-5 border border-recallio-light-gray/50">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Search size={16} />
                Example Queries
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {examplePrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setQuery(prompt)}
                    className="text-left text-sm p-3 rounded-md border border-recallio-light-gray/50 hover:bg-recallio-light-gray/20 transition-colors flex items-center gap-2 group"
                  >
                    <span className="text-muted-foreground">{prompt}</span>
                    <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                  </button>
                ))}
              </div>
            </div>

            {/* Results area */}
            {optimizedContext && (
              <div className="space-y-6">
                {/* Context summary */}
                <Card className="border border-recallio-light-gray/50">
                  <CardContent className="pt-6">
                    <h3 className="font-medium mb-4 flex items-center">
                      <FileText className="mr-2 h-5 w-5 text-recallio-yellow" />
                      Optimized Context
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-3">
                        <div className="bg-recallio-light-gray/30 px-3 py-1 rounded-full text-xs flex items-center">
                          <span className="font-geist-mono mr-2">Token Count:</span>
                          <span className="font-geist-mono font-medium">{optimizedContext.token_count} / {maxTokens}</span>
                        </div>
                        
                        <div className="bg-recallio-light-gray/30 px-3 py-1 rounded-full text-xs flex items-center">
                          <span className="font-geist-mono mr-2">Memories:</span>
                          <span className="font-geist-mono font-medium">{optimizedContext.memories.length}</span>
                        </div>
                        
                        <div className="bg-recallio-light-gray/30 px-3 py-1 rounded-full text-xs flex items-center">
                          <span className="font-geist-mono mr-2">Scoring:</span>
                          <span className="font-geist-mono font-medium">
                            {semanticWeight}% semantic / {100-semanticWeight}% freshness
                          </span>
                        </div>
                      </div>

                      {/* Context block */}
                      <CodeBlock
                        code={optimizedContext.context_optimized}
                        title="Optimized Context Block"
                      />

                      {/* Distilled summary (if enabled) */}
                      {enableDistillation && optimizedContext.summary && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium mb-2">Distilled Summary</h4>
                          <div className="bg-recallio-medium-gray rounded-md p-4 font-geist-mono text-sm whitespace-pre-line">
                            {optimizedContext.summary}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Memory sources */}
                <Collapsible className="border border-recallio-light-gray/50 rounded-lg">
                  <div className="p-5 flex items-center justify-between">
                    <h4 className="font-medium flex items-center">
                      <FileText className="mr-2 h-5 w-5" />
                      Memory Sources
                    </h4>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm">
                        Details
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  
                  <CollapsibleContent>
                    <div className="px-5 pb-5 space-y-3">
                      {optimizedContext.memories.map((memory: { id: string; timestamp: string; project: string; content: string; semantic_score?: number; freshness_score?: number }) => (
                        <div 
                          key={memory.id}
                          className="bg-recallio-dark-gray border border-recallio-light-gray/20 rounded-md p-3 text-sm"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">
                              {formatLocalDateTime(memory.timestamp)}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-recallio-light-gray/30">
                                {memory.project}
                              </span>
                            </div>
                          </div>
                          
                          <p className="font-geist-mono mb-2">{memory.content}</p>
                          
                          <div className="flex items-center gap-3 mt-2">
                            <div className="text-xs flex items-center">
                              <span className="mr-1">Semantic:</span>
                              <span className={`font-geist-mono ${memory.semantic_score > 0.8 ? 'text-recallio-green' : memory.semantic_score > 0.6 ? 'text-recallio-yellow' : 'text-red-400'}`}>
                                {(memory.semantic_score * 100).toFixed(0)}%
                              </span>
                            </div>
                            
                            <div className="text-xs flex items-center">
                              <span className="mr-1">Freshness:</span>
                              <span className={`font-geist-mono ${memory.freshness_score > 0.8 ? 'text-recallio-green' : memory.freshness_score > 0.6 ? 'text-recallio-yellow' : 'text-red-400'}`}>
                                {(memory.freshness_score * 100).toFixed(0)}%
                              </span>
                            </div>
                            
                            <div className="text-xs flex items-center">
                              <span className="mr-1">Combined Score:</span>
                              <span className="font-geist-mono">
                                {((memory.semantic_score * semanticWeight/100) + (memory.freshness_score * (100-semanticWeight)/100)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

            {/* Code implementation */}
            <div className="mt-4">
              <Collapsible open={openCodeExample} onOpenChange={setOpenCodeExample}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Implementation Example</h3>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm">
                      {openCodeExample ? "Hide Code" : "Show Code"}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                
                <CollapsibleContent className="mt-4">
                  <div className="border border-recallio-light-gray/50 rounded-lg overflow-hidden">
                    <div className="flex border-b border-recallio-light-gray/50">
                      <button 
                        className={`px-4 py-2 text-sm font-geist-mono ${activeCodeTab === 'javascript' ? 'bg-recallio-light-gray/30 text-recallio-yellow' : ''}`}
                        onClick={() => setActiveCodeTab('javascript')}
                      >
                        JavaScript
                      </button>
                      <button 
                        className={`px-4 py-2 text-sm font-geist-mono ${activeCodeTab === 'python' ? 'bg-recallio-light-gray/30 text-recallio-yellow' : ''}`}
                        onClick={() => setActiveCodeTab('python')}
                      >
                        Python
                      </button>
                    </div>
                    
                    <CodeBlock 
                      code={codeExamples[activeCodeTab]} 
                      language={activeCodeTab}
                    />
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button variant="outline" size="sm" className="text-xs flex items-center gap-2">
                      View Complete Documentation
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </TabsContent>
          
          <TabsContent value="docs">
            <Card className="border border-recallio-light-gray/50">
              <CardContent className="pt-6">
                <h2 className="text-xl font-medium mb-4">Context Orchestration Documentation</h2>
                
                <div className="prose prose-invert max-w-none">
                  <p className="text-muted-foreground">
                    The Context Orchestration layer helps you optimize your LLM prompts by intelligently selecting 
                    and formatting the most relevant memories. Our advanced algorithms combine semantic relevance
                    and recency to create the perfect context block for any query.
                  </p>
                  
                  <h3 className="text-lg font-medium mt-6 mb-3">Key Features</h3>
                  
                  <ul className="space-y-2 list-disc pl-6">
                    <li>
                      <strong>Smart Memory Selection:</strong> Uses semantic search and freshness scoring to find the most relevant memories
                    </li>
                    <li>
                      <strong>Token Budget Management:</strong> Automatically stays within your token limits without losing critical context
                    </li>
                    <li>
                      <strong>Context Distillation:</strong> Uses AI to summarize memories when needed to maximize information density
                    </li>
                    <li>
                      <strong>Flexible Scoring:</strong> Adjust the balance between semantic relevance and recency for your specific use case
                    </li>
                    <li>
                      <strong>Full Transparency:</strong> Get detailed scoring and source information for auditability
                    </li>
                  </ul>
                  
                  <h3 className="text-lg font-medium mt-6 mb-3">Getting Started</h3>
                  
                  <ol className="space-y-3 list-decimal pl-6">
                    <li>Install the Recallio SDK for your preferred language</li>
                    <li>Configure your client with your API key</li>
                    <li>Use the context.optimize() method to generate optimized context</li>
                    <li>Inject the returned context block into your LLM prompt</li>
                  </ol>
                  
                  <div className="mt-6 p-4 bg-recallio-dark-gray rounded-lg border border-recallio-light-gray/20">
                    <h4 className="font-medium mb-2">Need more help?</h4>
                    <p className="text-sm text-muted-foreground">Check out our detailed API reference and implementation guides in the documentation.</p>
                    <Button variant="outline" size="sm" className="mt-3">
                      View Full Documentation
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default ContextPlayground;
